import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { eventDay } from '../lib/eventStats';
import useActivations from '@/src/Activations/components/hooks/useActivations';
import useProduction from '@/src/Production/components/hooks/useProduction';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

/*
 * JumpMenu — additive quick-nav in the top bar (spec: nav-redesign
 * handoff + Josh's search extension). It does NOT replace the context
 * switchers; it sits beside them. Trigger is a mono-caps JUMP pill on
 * the Jobs/Activations pages and a plain magnifier on the Dashboard.
 * Search live-filters activations (name + venue) and jobs (name +
 * client); with no query it shows LIVE NOW / TODAY & TOMORROW / JOBS.
 * Data loads lazily on open; any navigation closes and clears.
 */

const LIVE_WINDOW_MIN = 45;

interface JumpRow {
    key: string;
    name: string;
    detail: string;
    live?: boolean;
    to: string;
}

const IconSearch = ({ size = 18 }: { size?: number }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
    >
        <circle cx="11" cy="11" r="6.5" />
        <path d="M15.8 15.8 20 20" />
    </svg>
);

export default function JumpMenu() {
    const navigate = useNavigate();
    const { orgId } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();
    const { getActivations } = useActivations();
    const { getJobs } = useProduction();

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [acts, setActs] = useState<any[]>([]);
    const [jobs, setJobs] = useState<any[]>([]);
    const [liveIds, setLiveIds] = useState<Set<number>>(new Set());
    const rootRef = useRef<HTMLDivElement>(null);

    /* lazy load on each open — three light gets */
    useEffect(() => {
        if (!open) return;
        let mounted = true;
        getActivations()
            .then((rows: any[]) => mounted && setActs(rows ?? []))
            .catch(() => {});
        getJobs()
            .then((rows: any[]) => mounted && setJobs(rows ?? []))
            .catch(() => {});
        if (orgId) {
            authenticatedApi
                .get(`${orgId}/media/latest?`)
                .then((r) => {
                    if (!mounted) return;
                    setLiveIds(
                        new Set(
                            (r.data ?? [])
                                .filter(
                                    (f: any) =>
                                        moment().diff(
                                            moment(f.createdAt),
                                            'minutes',
                                        ) < LIVE_WINDOW_MIN,
                                )
                                .map((f: any) => f.activationId),
                        ),
                    );
                })
                .catch(() => {});
        }
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) {
            setQuery('');
            return;
        }
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const go = (to: string) => {
        setOpen(false);
        setQuery('');
        navigate(to);
    };

    const activationRow = (a: any): JumpRow => {
        const live = liveIds.has(a.id);
        const d = eventDay(a.date);
        return {
            key: `a${a.id}`,
            name: a.name,
            live,
            detail: live
                ? 'LIVE'
                : d.isSame(moment(), 'day')
                  ? 'TODAY'
                  : d.isSame(moment().add(1, 'day'), 'day')
                    ? 'TOMORROW'
                    : d.format('MMM D').toUpperCase(),
            to: `/v2/activations/${a.id}`,
        };
    };

    const jobRow = (j: any): JumpRow => ({
        key: `j${j.jobId}`,
        name: j.name,
        detail: `${j.activationCount} STOP${
            +j.activationCount === 1 ? '' : 'S'
        }`,
        to: `/v2/jobs/${j.jobId}`,
    });

    const current = acts.filter((a: any) => !a.isArchived);
    const realJobs = jobs.filter((j: any) => j.jobId != null);

    let groups: Array<{ label: string; rows: JumpRow[] }>;
    const q = query.trim().toLowerCase();
    if (q) {
        const hitA = current
            .filter((a: any) =>
                `${a.name} ${a.location ?? ''}`.toLowerCase().includes(q),
            )
            .slice(0, 6)
            .map(activationRow);
        const hitJ = realJobs
            .filter((j: any) =>
                `${j.name} ${j.billingEntity ?? ''}`.toLowerCase().includes(q),
            )
            .slice(0, 4)
            .map(jobRow);
        groups = [
            { label: `Results · ${hitA.length + hitJ.length}`, rows: [...hitA, ...hitJ] },
        ];
    } else {
        const live = current
            .filter((a: any) => liveIds.has(a.id))
            .slice(0, 5)
            .map(activationRow);
        const soon = current
            .filter(
                (a: any) =>
                    !liveIds.has(a.id) &&
                    (eventDay(a.date).isSame(moment(), 'day') ||
                        eventDay(a.date).isSame(
                            moment().add(1, 'day'),
                            'day',
                        )),
            )
            .sort(
                (a: any, b: any) =>
                    eventDay(a.date).valueOf() - eventDay(b.date).valueOf(),
            )
            .map(activationRow);
        groups = [
            ...(live.length
                ? [{ label: `Live now · ${live.length}`, rows: live }]
                : []),
            ...(soon.length
                ? [{ label: 'Today & tomorrow', rows: soon }]
                : []),
            {
                label: 'Jobs',
                rows: realJobs.slice(0, 5).map(jobRow),
            },
        ].filter((g) => g.rows.length);
    }

    return (
        <div ref={rootRef} className="relative flex">
            {/* sized to sit beside the 36px avatar */}
            <button
                type="button"
                title="Jump to…"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-faint hover:bg-secondary hover:text-foreground"
            >
                <IconSearch />
            </button>
            {/* right-aligned under the trigger; width caps at the
                viewport so mobile gets effectively full width */}
            {open && (
                <div
                    style={{
                        width: 440,
                        maxWidth: 'calc(100vw - 76px)',
                    }}
                    className="absolute right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-[14px] border border-border bg-background p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.12)]"
                >
                    <div className="mb-1 flex items-center gap-2 border-b border-line px-2.5 pb-2 pt-1">
                        <span className="flex-none text-faint">
                            <IconSearch size={14} />
                        </span>
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            aria-label="Jump to activation or job"
                            className="w-full border-0 bg-transparent p-0 font-[inherit] text-[13px] text-foreground outline-none"
                        />
                    </div>
                    <div className="max-h-[340px] overflow-y-auto overflow-x-hidden">
                        {q && groups[0].rows.length === 0 && (
                            <div className="px-2.5 py-2 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                No matches
                            </div>
                        )}
                        {groups.map((g) => (
                            <div key={g.label}>
                                {g.rows.length > 0 && (
                                    <div className="px-2.5 pb-0.5 pt-2 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                        {g.label}
                                    </div>
                                )}
                                {g.rows.map((r) => (
                                    <button
                                        key={r.key}
                                        type="button"
                                        onClick={() => go(r.to)}
                                        className="flex w-full cursor-pointer items-center gap-2 rounded-[9px] border-0 bg-transparent px-2.5 py-2 text-left font-[inherit] text-[13px] hover:bg-secondary"
                                    >
                                        {r.live && (
                                            <span
                                                className="ps-pulse ps-pulse-live flex-none"
                                                style={{
                                                    width: 6,
                                                    height: 6,
                                                }}
                                            />
                                        )}
                                        <span
                                            className={
                                                'w-0 flex-1 truncate ' +
                                                (r.live ? 'font-bold' : '')
                                            }
                                        >
                                            {r.name}
                                        </span>
                                        <span className="ml-auto flex-none whitespace-nowrap text-right font-mono text-[10px] text-faint">
                                            {r.detail}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
